import type { CustomizeRequest, CustomizeResponse } from '~/contracts/new-cars';
import { isDefined } from '~/utilities/array';
import optimizeImage, { type Options } from '~/utilities/optimizeImage';

// The cube is a remote display, so it can't reach the configurator's in-memory
// option catalog. We emit the selected item's CDN image URL (mapped to Fastly),
// name, code, price, and description directly in each event — the cube renders
// what it's told rather than fetching the catalog itself. Sized generously for a
// large display; adjust here if the cube viewport changes.
export const CUBE_IMAGE_OPTIONS: Options = {
  format: 'auto',
  optimize: 'high',
  width: 1024
};

export type CubeSelectionValue = {
  code: string;
  description: string | null;
  imageUrl: string;
  name: string;
  priceAdd: number;
};

export type CubeSelections = {
  driveType: CubeSelectionValue | null;
  engine: CubeSelectionValue | null;
  exteriorColor: CubeSelectionValue | null;
  interiorColor: CubeSelectionValue | null;
  packages: CubeSelectionValue[];
};

export type CubeCatalog = {
  driveTypes: CubeSelectionValue[];
  engines: CubeSelectionValue[];
  exteriorColors: CubeSelectionValue[];
  interiorColors: CubeSelectionValue[];
  packages: CubeSelectionValue[];
};

export type CubeSelectionField = keyof CubeSelections;

type CustomizeLikeItem = {
  code?: string;
  description?: string;
  imageUrl: string;
  name: string;
  priceAdd: number;
};

function toSelectionValue(item: CustomizeLikeItem): CubeSelectionValue {
  return {
    code: item.code ?? item.name,
    description: item.description ?? null,
    imageUrl: optimizeImage(item.imageUrl, CUBE_IMAGE_OPTIONS),
    name: item.name,
    priceAdd: item.priceAdd
  };
}

export function buildCubeSelections(
  selectedOptions: CustomizeRequest | null,
  customizeData: CustomizeResponse
): CubeSelections {
  if (!selectedOptions) {
    return {
      driveType: null,
      engine: null,
      exteriorColor: null,
      interiorColor: null,
      packages: []
    };
  }

  const driveType = customizeData.driveTypes.find(
    item => item.name === selectedOptions.driveType
  );
  const engine = customizeData.engines.find(
    item => item.code === selectedOptions.engineCode
  );
  const exteriorColor = customizeData.exteriorColors.find(
    item => item.code === selectedOptions.exteriorColorCode
  );
  const interiorColor = customizeData.interiorColors.find(
    item => item.code === selectedOptions.interiorColorCode
  );

  const selectedPackageCodes = selectedOptions.packageOptionCodes ?? [];
  const packages = selectedPackageCodes
    .map(code => customizeData.packageOptions.find(item => item.code === code))
    .filter(isDefined)
    .map(toSelectionValue);

  return {
    driveType: driveType ? toSelectionValue(driveType) : null,
    engine: engine ? toSelectionValue(engine) : null,
    exteriorColor: exteriorColor ? toSelectionValue(exteriorColor) : null,
    interiorColor: interiorColor ? toSelectionValue(interiorColor) : null,
    packages
  };
}

export function buildCubeCatalog(
  customizeData: CustomizeResponse
): CubeCatalog {
  return {
    driveTypes: customizeData.driveTypes.map(toSelectionValue),
    engines: customizeData.engines.map(toSelectionValue),
    exteriorColors: customizeData.exteriorColors.map(toSelectionValue),
    interiorColors: customizeData.interiorColors.map(toSelectionValue),
    packages: customizeData.packageOptions.map(toSelectionValue)
  };
}

// Stable identity for a catalog, used to decide whether to re-send it. Keyed on
// the full emitted content (not just codes): a trim switch can return the same
// option codes with different images, prices, or names, and the cube must
// re-prefetch in that case. Same content reload → same key (skip), any change →
// new key (re-send). Key order in each value is fixed by toSelectionValue and
// array order is made deterministic by the client-side sorts in
// useGetCustomizeOptions (the API itself does not guarantee order), so this is
// stable.
export function catalogKey(catalog: CubeCatalog): string {
  return JSON.stringify(catalog);
}

function valueCode(value: CubeSelectionValue | null): string {
  return value ? value.code : '';
}

function packagesKey(values: CubeSelectionValue[]): string {
  return values
    .map(value => value.code)
    .sort()
    .join(',');
}

const SCALAR_FIELDS = [
  'driveType',
  'engine',
  'exteriorColor',
  'interiorColor'
] as const;

// Returns the selection fields that changed between two snapshots. An empty
// array means either nothing changed or this is the initial snapshot (prev is
// null) — the caller decides whether to still emit it. Packages compare by the
// set of selected codes; everything else by code.
export function diffCubeSelections(
  previous: CubeSelections | null,
  next: CubeSelections
): CubeSelectionField[] {
  if (!previous) return [];

  const changed: CubeSelectionField[] = [];

  for (const field of SCALAR_FIELDS) {
    if (valueCode(previous[field]) !== valueCode(next[field])) {
      changed.push(field);
    }
  }

  if (packagesKey(previous.packages) !== packagesKey(next.packages)) {
    changed.push('packages');
  }

  return changed;
}
